"use client";

import React from "react";
import { X } from "lucide-react"; // cancel icon
import "./ProgressCircle.css";

export type ProgressCircleProps = {
  /** 0..100 (if undefined/null => indeterminate) */
  value?: number | null;
  /** Diameter in px */
  size?: number;
  /** Stroke width in px */
  stroke?: number;
  /** Text label for screen readers and UI */
  label?: string;
  /** Show numeric percent text */
  showPercent?: boolean;
  /** Show cancel X overlay */
  cancelable?: boolean;
  /** Cancel handler */
  onCancel?: () => void;
  /** Round stroke linecap */
  rounded?: boolean;
  /** Soft glow using brand filter */
  glow?: boolean;
  /** Layout: inline | stacked | overlay (percent inside ring) */
  layout?: "inline" | "stacked" | "overlay";
  /** Custom className */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  /** Custom track color (fallback to CSS var) */
  trackColor?: string;
  /** Custom gradient stops */
  gradient?: { from?: string; mid?: string; to?: string };
  /** Force indeterminate mode (spinning) */
  indeterminate?: boolean;
  /** A11y label for cancel */
  cancelLabel?: string;
  /** Percent suffix (e.g., "%") */
  percentSuffix?: string;
  /** Cancel icon size */
  cancelIconSize?: number;
  /** Dimmed backdrop behind the ring (for overlay usage) */
  backdrop?: boolean;
  /** Backdrop opacity (0..1) */
  backdropOpacity?: number;
  /** Backdrop blur in px */
  backdropBlur?: number;
};

export const ProgressCircle: React.FC<ProgressCircleProps> = ({
  value,
  size = 68,
  stroke = 6,
  label = "Uploading",
  showPercent = true,
  cancelable = true,
  onCancel,
  rounded = true,
  glow = true,
  layout = "inline",
  className,
  style,
  trackColor,
  gradient,
  indeterminate,
  cancelLabel = "Cancel upload",
  percentSuffix = "%",
  cancelIconSize = 16,
  backdrop = false,
  backdropOpacity = 0.45,
  backdropBlur = 3,
}) => {
  const id = React.useId();
  const isIndeterminate = indeterminate || value === undefined || value === null;
  const pct = Math.max(0, Math.min(100, Number(value ?? 0)));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;

  // ARIA: avoid aria-valuenow for indeterminate
  const ariaProps = isIndeterminate
    ? { "aria-busy": true }
    : { "aria-valuenow": Math.round(pct), "aria-valuemin": 0, "aria-valuemax": 100 };

  return (
    <div
      className={[
        "pc-container",
        glow ? "pc-glow" : "",
        layout === "overlay" ? "pc-overlay" : "",
        className || "",
      ].join(" ").trim()}
      role="progressbar"
      aria-label={`${label}${!isIndeterminate && showPercent ? ` ${Math.round(pct)}${percentSuffix}` : ""}`}
      {...ariaProps}
      style={style}
    >
      {/* optional dim backdrop (only useful for layout="overlay") */}
      {backdrop && layout === "overlay" && (
        <div
          className="pc-backdrop"
          style={{
            background: `rgba(18,18,18,${backdropOpacity})`,
            backdropFilter: `blur(${backdropBlur}px)`,
            WebkitBackdropFilter: `blur(${backdropBlur}px)`,
          }}
        />
      )}
      <div className="pc-ring-wrap" style={{ width: size, height: size }}>
        <svg
          className={`pc-ring-svg ${isIndeterminate ? "pc-spin" : ""}`}
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
        >


          {/* Track */}
          <circle
            className="pc-track"
            cx={size / 2}
            cy={size / 2}
            r={r}
            strokeWidth={stroke}
            style={trackColor ? { stroke: trackColor } : undefined}
          />

          {/* Progress */}
          <circle
            className={`pc-bar ${isIndeterminate ? "pc-bar-ind" : ""}`}
            cx={size / 2}
            cy={size / 2}
            r={r}
            strokeWidth={stroke}
            strokeLinecap={rounded ? "round" : "butt"}
            strokeDasharray={c}
            strokeDashoffset={isIndeterminate ? c * 0.75 : offset}
          /* stroke comes from CSS */
          /* optional: you can set stroke="currentColor" too, but CSS rule is enough */
          />
        </svg>

        {/* Corner X overlay (always near the ring) */}
        {cancelable && typeof onCancel === "function" && (
          <button
            type="button"
            className="pc-x"
            onClick={onCancel}
            aria-label={cancelLabel}
            // Optional: smaller X for small rings
            style={{ width: Math.max(24, Math.min(28, size * 0.4)), height: Math.max(24, Math.min(28, size * 0.4)) }}
          >
            <X size={cancelIconSize} strokeWidth={3} />
          </button>
        )}

        {/* Percent/label inside ring for overlay layout */}
        {layout === "overlay" && (
          <div className="pc-overlay-center">
            {!isIndeterminate && showPercent ? (
              <span className="pc-pct">{Math.round(pct)}{percentSuffix}</span>
            ) : (
              <span className="pc-label">{label}</span>
            )}
          </div>
        )}
      </div>

      {/* Meta text for inline/stacked layouts (no textual cancel anymore) */}
      {layout !== "overlay" && (
        <div className={`pc-meta ${layout === "stacked" ? "pc-meta-stacked" : ""}`}>
          <div className="pc-text">
            {!isIndeterminate && showPercent ? (
              <span className="pc-pct">{Math.round(pct)}{percentSuffix}</span>
            ) : (
              <span className="pc-label">{label}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressCircle;
